import { useState, useEffect } from 'react'
import { Hub } from '@aws-amplify/core'
import axios from 'axios'
import { UiStateStore } from './types'
import WhoAmIModel from 'models/WhoAmI'

// Cache for whoami response
let whoamiCache: any = null
let whoamiPromise: Promise<any> | null = null

const fetchWhoami = async () => {
  // If there's already a request in flight, return that promise
  if (whoamiPromise) {
    return whoamiPromise
  }

  // If we have cached data, return it
  if (whoamiCache) {
    return whoamiCache
  }

  // Make the API call and cache the promise
  whoamiPromise = axios.post('users/whoami')
    .then(({data}) => {
      const whoami = new WhoAmIModel().fromData(data)
      whoamiCache = whoami
      whoamiPromise = null
      return whoami
    })
    .catch(() => {
      whoamiPromise = null
      throw new Error('Unauthenticated')
    })

  return whoamiPromise
}

export const useUiState = (): UiStateStore => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<any>('')
  const [whoami, setWhoami] = useState<any>(whoamiCache)
  const [activeMerchant, setActiveMerchant] = useState<any>()

  useEffect(() => {
      // Only fetch if we don't have cached data
      if (!whoamiCache) {
          setLoading(true)

          // invalidate first to make sure we get fresh data
          invalidate('whoami')

          fetchWhoami()
              .then((data) => {
                  setWhoami(data)
                  setLoading(false)
              })
              .catch(() => {
                  setError('Unauthenticated')
                  setLoading(false)
              })
      }

      const unsubscribe = Hub.listen('auth', (data) => {
          if (
              data.payload.event === 'signIn' ||
              data.payload.event === 'signOut'
          ) {
              // Clear cache on auth events
              whoamiCache = null
              whoamiPromise = null
              invalidate('whoami')
          }
      })

      return () => {
          unsubscribe()
      }
  }, [])

  const invalidate = async (type: 'whoami') => {
    if (type === 'whoami') {
      try {
        setLoading(true)
        whoamiCache = null
        whoamiPromise = null
        const data = await fetchWhoami()
        setWhoami(data)
      } catch {
        setError('Unauthenticated')
      } finally {
        setLoading(false)
      }
    }
  }

  const setActiveMerchant_ = async (merchantId: number | null) => {
    try {
      setLoading(true)
      await axios.get(
        `cm/merchants/${merchantId ?? whoami?.merchant?.id}`
      ).then(({ data }: any) => {
        setActiveMerchant(data)
      })
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  return {
    whoami,
    activeMerchant,
    setActiveMerchant: setActiveMerchant_,
    loading,
    error,
    invalidate
  }
}
